<script lang="ts">
  import type { Fixture } from '../lib/api/client.ts';
  import { ticker } from '../lib/ticker.svelte.ts';

  /**
   * A line of scores across the top of the picture. When it fits, it simply
   * stands still. When it does not, the content is laid out twice and the
   * strip moves by exactly one copy, so the loop has no seam -- always at the
   * same speed however long the line is. Only a transform is animated: the
   * compositor does that without touching layout, which matters next to two
   * decoding videos on a TV box.
   */

  let copyWidth = $state(0);
  let tickerWidth = $state(0);
  let screenWidth = $state(window.innerWidth);
  let strip: HTMLDivElement | undefined = $state();

  const runs = $derived(copyWidth > 0 && tickerWidth > 0 && copyWidth > tickerWidth);

  /** Crests that failed to load; those matches fall back to the name. */
  let brokenLogos = $state(new Set<string>());

  /*
   * Driven through the Web Animations API rather than a CSS duration: when a
   * match is added the copy gets longer, and a new CSS duration would make the
   * strip jump. Here the new animation continues at the pixel the old one had
   * reached.
   */
  let animation: Animation | null = null;
  let animatedWidth = 0;

  $effect(() => {
    const el = strip;
    const width = copyWidth;
    const pxPerS = screenWidth * ticker.style.speed;
    if (!el || !runs || pxPerS <= 0) {
      animation?.cancel();
      animation = null;
      return;
    }
    let offset = 0;
    if (animation && animatedWidth > 0) {
      const t = Number(animation.currentTime ?? 0);
      const d = Number(animation.effect?.getTiming().duration ?? 0);
      if (d > 0) offset = ((t % d) / d) * animatedWidth;
      animation.cancel();
    }
    const duration = (width / pxPerS) * 1000;
    animation = el.animate(
      [{ transform: 'translateX(0)' }, { transform: `translateX(${-width}px)` }],
      { duration, iterations: Infinity, easing: 'linear' },
    );
    animation.currentTime = ((offset % width) / width) * duration;
    animatedWidth = width;
  });

  $effect(() => () => animation?.cancel());

  /** Grouped by competition, so a mixed ticker still reads in blocks. */
  const groups = $derived.by(() => {
    const out: Array<{ competition: string; fixtures: Fixture[] }> = [];
    for (const f of ticker.visible) {
      const last = out.at(-1);
      if (last?.competition === f.competition) last.fixtures.push(f);
      else out.push({ competition: f.competition, fixtures: [f] });
    }
    return out;
  });
  const showCompetition = $derived(groups.length > 1);

  const time = (ms: number) =>
    new Date(ms).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  function score(f: Fixture): string {
    if (f.status === 'scheduled') return time(f.kickoff);
    return `${f.homeScore ?? 0}:${f.awayScore ?? 0}`;
  }
</script>

<svelte:window bind:innerWidth={screenWidth} />

{#snippet team(name: string, logo: string | null | undefined)}
  {#if ticker.style.logos && logo && !brokenLogos.has(logo)}
    <img
      class="crest"
      src={logo}
      alt={name}
      title={name}
      onerror={() => (brokenLogos = new Set(brokenLogos).add(logo))}
    />
  {:else}
    <span>{name}</span>
  {/if}
{/snippet}

{#snippet line()}
  {#each groups as group (group.competition)}
    {#if showCompetition}<span class="competition">{group.competition}</span>{/if}
    {#each group.fixtures as f (f.provider + f.externalId)}
      <span class="match" class:live={f.status === 'live'} class:done={f.status === 'finished'} class:hot={ticker.isHighlighted(f)}>
        {#if f.status === 'live'}<span class="dot"></span>{/if}
        {@render team(f.homeTeam, f.extra?.homeLogo)}
        <b class="score">{score(f)}</b>
        {@render team(f.awayTeam, f.extra?.awayLogo)}
        {#if f.status === 'finished'}<span class="faint">Ende</span>{/if}
      </span>
    {/each}
  {/each}
{/snippet}

{#if ticker.visible.length > 0}
  <div class="ticker" aria-label="Ergebnisse" style:--size={ticker.style.size} bind:clientWidth={tickerWidth}>
    <div class="strip" class:still={!runs} bind:this={strip}>
      <div class="copy" bind:offsetWidth={copyWidth}>{@render line()}</div>
      {#if runs}<div class="copy" aria-hidden="true">{@render line()}</div>{/if}
    </div>
  </div>
{/if}

<style>
  .ticker {
    position: absolute;
    left: 0; right: 0; top: 0;
    z-index: 12;
    overflow: hidden;
    pointer-events: none;
    padding: calc(var(--safe-inset) * 0.5) 0 0.3em;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.65) 75%, transparent 100%);
    font-size: calc(var(--font-sm) * var(--size, 1));
    white-space: nowrap;
  }

  .strip {
    display: inline-flex;
    will-change: transform;
  }

  /* Standing still, from the left edge like the running line starts. */
  .strip.still { will-change: auto; }

  /* Never squeezed, or an overlong line would measure as fitting. */
  .copy { display: inline-flex; align-items: center; flex: none; }

  .competition {
    margin: 0 0.6em 0 1.6em;
    color: var(--text-dim);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 0.85em;
  }

  .match { display: inline-flex; align-items: center; gap: 0.35em; margin-right: 2.2em; }
  .crest { width: 1.5em; height: 1.5em; object-fit: contain; }
  .score { font-variant-numeric: tabular-nums; padding: 0 0.15em; }
  .done { opacity: 0.7; }
  .hot .score { color: #111; background: var(--accent); border-radius: 0.2em; padding: 0 0.35em; }

  .dot {
    width: 0.5em; height: 0.5em;
    border-radius: 50%;
    background: var(--danger);
  }
</style>
